import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, switchMap, map, catchError, of } from 'rxjs';

import { FiltersDomainService, EntityFiltersDomainService } from '@apps/prtg/domain';
import { EntityFilters } from '@apps/prtg/models';
import { NotfGravity, NotificationService, NotifType } from '@common/services';

import * as fromPrtgActions from './prtg.actions';
import { NoContentResult } from '@common/domain/models/no-content-result';

@Injectable()
export class BackPrtgEffects
{
    constructor (
        private actions: Actions,
        private entityFiltersDomainService: EntityFiltersDomainService,
        private filtersDomainService: FiltersDomainService,
        private notificationService: NotificationService,
    )
    { }

    backPrtgFiltersRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromPrtgActions.BackPrtgFiltersRequested),
                    switchMap(() =>
                        this.filtersDomainService.getFilters()
                            .pipe(
                                map((filters: string[]) =>
                                    fromPrtgActions.BackPrtgFiltersSucceded({ filters })
                                ),
                                catchError(() =>
                                    of(fromPrtgActions.BackPrtgFiltersFailed())
                                )
                            )
                    )
                )
    );
    backPrtgEntityFiltersCreateRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromPrtgActions.BackPrtgEntityFiltersCreateRequested),
                    switchMap(({ entityFilters }: { entityFilters: EntityFilters }) =>
                        this.entityFiltersDomainService.createEntityFilters(entityFilters)
                            .pipe(
                                map(() =>
                                    fromPrtgActions.BackPrtgEntityFiltersCreateSucceeded()
                                ),
                                catchError(() =>
                                    of(fromPrtgActions.BackPrtgEntityFiltersCreateFailed())
                                )
                            )
                    )
                )
    );

    backPrtgEntityFiltersCreateSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromPrtgActions.BackPrtgEntityFiltersCreateSucceeded),
                    map(() =>
                    {
                        this.notificationService.notify(
                            [
                                '',
                                'ADMIN.PRTG.NOTF.CREATE.MSGSUCCESS'
                            ],
                            NotfGravity.success,
                            NotifType.SNACKBAR
                        );
                    })
                ),
        { dispatch: false }
    );

    backPrtgEntityFiltersCreateFailed = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromPrtgActions.BackPrtgEntityFiltersCreateFailed),
                    map(() =>
                    {
                        this.notificationService.notify(
                            [
                                '',
                                'ADMIN.PRTG.NOTF.CREATE.MSGFAILED'
                            ],
                            NotfGravity.danger,
                            NotifType.SNACKBAR
                        );
                    })
                ),
        { dispatch: false }
    );

    backPrtgEntityFiltersRetrieveRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromPrtgActions.BackPrtgEntityFiltersRetrieveRequested),
                    switchMap(({ entityId }: { entityId: string }) =>
                        this.entityFiltersDomainService.retrieveEntityFilters(entityId)
                            .pipe(
                                map((result: NoContentResult<EntityFilters>) =>
                                    fromPrtgActions.BackPrtgEntityFiltersRetrieveSucceeded({ result })
                                ),
                                catchError(() =>
                                    of(fromPrtgActions.BackPrtgEntityFiltersRetrieveFailed())
                                )
                            )
                    )
                )
    );

    backPrtgEntityFiltersUpdateRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromPrtgActions.BackPrtgEntityFiltersUpdateRequested),
                    switchMap(({ entityFilters }: { entityFilters: EntityFilters }) =>
                        this.entityFiltersDomainService.updateEntityFilters(entityFilters)
                            .pipe(
                                map(() =>
                                    fromPrtgActions.BackPrtgEntityFiltersUpdateSucceeded()
                                ),
                                catchError(() =>
                                    of(fromPrtgActions.BackPrtgEntityFiltersUpdateFailed())
                                )
                            )
                    )
                )
    );

    backPrtgEntityFiltersUpdateSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromPrtgActions.BackPrtgEntityFiltersUpdateSucceeded),
                    map(() =>
                    {
                        this.notificationService.notify(
                            [
                                '',
                                'ADMIN.PRTG.NOTF.UPDATE.MSGSUCCESS'
                            ],
                            NotfGravity.success,
                            NotifType.SNACKBAR
                        );
                    })
                ),
        { dispatch: false }
    );

    backPrtgEntityFiltersUpdateFailed = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromPrtgActions.BackPrtgEntityFiltersUpdateFailed),
                    map(() =>
                    {
                        this.notificationService.notify(
                            [
                                '',
                                'ADMIN.PRTG.NOTF.UPDATE.MSGFAILED'
                            ],
                            NotfGravity.danger,
                            NotifType.SNACKBAR
                        );
                    })
                ),
        { dispatch: false }
    );

    backPrtgEntityFiltersDeleteRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromPrtgActions.BackPrtgEntityFiltersDeleteRequested),
                    switchMap(({ entityId }: {entityId: string}) =>
                        this.entityFiltersDomainService.deleteEntityFilters(entityId)
                            .pipe(
                                map(() =>
                                    fromPrtgActions.BackPrtgEntityFiltersDeleteSucceeded()
                                ),
                                catchError(() =>
                                    of(fromPrtgActions.BackPrtgEntityFiltersDeleteFailed())
                                )
                            )
                    )
                )
    );

    backPrtgEntityFiltersDeleteSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromPrtgActions.BackPrtgEntityFiltersDeleteSucceeded),
                    map(() =>
                    {
                        this.notificationService.notify(
                            [
                                '',
                                'ADMIN.PRTG.NOTF.DELETE.MSGSUCCESS'
                            ],
                            NotfGravity.success,
                            NotifType.SNACKBAR
                        );
                    })
                ),
        { dispatch: false }
    );

    backPrtgEntityFiltersDeleteFailed = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromPrtgActions.BackPrtgEntityFiltersDeleteFailed),
                    map(() =>
                    {
                        this.notificationService.notify(
                            [
                                '',
                                'ADMIN.PRTG.NOTF.DELETE.MSGFAILED'
                            ],
                            NotfGravity.danger,
                            NotifType.SNACKBAR
                        );
                    })
                ),
        { dispatch: false }
    );
}
